// Keep this loose: the server re-validates, and a client refusal the server would accept is a
// dead end.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// FILTER_VALIDATE_EMAIL refuses 255 characters and more: catch it here, not after the submit.
export const MAX_EMAIL_LENGTH = 254;
const CURRENCY_RE = /^[A-Z]{3}$/;
// Shape only: the server also checks the code is assigned, and the select offers no other value.
const COUNTRY_RE = /^[A-Z]{2}$/;
// Mirrors SubmitDonationForm::INTERNATIONAL_PHONE_PATTERN.
const INTERNATIONAL_PHONE_RE = /^[^+\uFF0B\p{Nd}]*(?:[+\uFF0B]|00)/u;
export const CIVILITIES = ["Mr", "Mrs", "Ms", "Dr", "Other"];
// Mirrors HandoffFields.php: UTF-16 code units of the trimmed value, like the server.
export const MAX_LENGTH = {
    name: 243,
    addressLine: 500,
    postalCode: 20,
    city: 100,
    orgName: 487,
    siret: 50,
    legalForm: 100,
};
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DONOR_TYPES = new Set(["Individual", "Organization"]);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// Mirrors HandoffFields::isBlank(): whitespace or control characters only.
const blank = (v) => /^[\s\p{Cc}]*$/u.test(v ?? "");
const tooLong = (v, max) => (v ?? "").trim().length > max;
// Compare the parse back to the input: new Date("2026-02-30T00:00:00Z") rolls over to March 2nd
// instead of failing.
// Raw value, no trim — the server regex does not trim either.
function isCalendarDate(value) {
    if (!ISO_DATE_RE.test(value)) {
        return false;
    }
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
function activePresetsFor(doc, freq) {
    return doc.amountPresets.filter((p) => p.frequency === freq && p.isActive);
}
// Must stay equal to the engine's amountMinor resolution (context/donationContext.ts); duplicated
// to keep this file engine-free.
function resolveAmountMinor(state, allowCustom) {
    if (state.custom !== "" && allowCustom) {
        const major = parseInt(state.custom, 10);
        return Number.isNaN(major) ? 0 : major * 100;
    }
    return state.amountMinor;
}
function validateAmount(state, doc, errors) {
    const freq = state.freq;
    const allowed = doc.allowedFrequencies.find((f) => f.frequency === freq);
    const allowCustom = allowed?.allowCustomAmount ?? false;
    const amountMinor = resolveAmountMinor(state, allowCustom);
    const currency = doc.defaultCurrency;
    if (amountMinor <= 0) {
        errors.amount = "amount.invalid";
        return;
    }
    const presets = activePresetsFor(doc, freq);
    const presetMatch = presets.some((p) => p.amountMinor === amountMinor && p.currency === currency);
    if (presetMatch) {
        return;
    }
    if (!allowCustom) {
        errors.amount = presets.length > 0 ? "amount.customDisabled" : "amount.notOffered";
        return;
    }
    // typeof, not !== null: a partial preview document can carry undefined or a non-number here.
    const min = doc.settings.minimumAmountMinor;
    if (typeof min === "number" && amountMinor < min) {
        errors.amount = "amount.belowMinimum";
    }
}
// Every offer gate below uses the engine's own expression on the set it renders from
// (context/donationContext.ts): a gate on a control the UI never renders is a dead end.
export function validateDonor(state, doc) {
    const errors = {};
    validateAmount(state, doc, errors);
    if (!CURRENCY_RE.test(doc.defaultCurrency)) {
        errors.currency = "currency.invalid";
    }
    // Offered only: an offered recurring frequency stays selectable, the server refuses it at submit.
    const freqOffered = doc.allowedFrequencies.some((f) => f.frequency === state.freq);
    if (!freqOffered) {
        errors.frequency = "frequency.notOffered";
    }
    if (!DONOR_TYPES.has(state.donorType)) {
        errors.donorType = "donorType.invalid";
    }
    if (blank(state.firstName)) {
        errors.firstName = "firstName.required";
    }
    else if (tooLong(state.firstName, MAX_LENGTH.name)) {
        errors.firstName = "firstName.tooLong";
    }
    if (blank(state.lastName)) {
        errors.lastName = "lastName.required";
    }
    else if (tooLong(state.lastName, MAX_LENGTH.name)) {
        errors.lastName = "lastName.tooLong";
    }
    if (!EMAIL_RE.test((state.email ?? "").trim()) || tooLong(state.email, MAX_EMAIL_LENGTH)) {
        errors.email = "email.invalid";
    }
    // Raw value, no trim: a blank civility is never sent, any other value must be a member name.
    if (!blank(state.civility) && !CIVILITIES.includes(state.civility)) {
        errors.civility = "civility.invalid";
    }
    if (blank(state.addressLine1)) {
        errors.addressLine1 = "addressLine1.required";
    }
    else if (tooLong(state.addressLine1, MAX_LENGTH.addressLine)) {
        errors.addressLine1 = "addressLine1.tooLong";
    }
    if (tooLong(state.addressLine2, MAX_LENGTH.addressLine)) {
        errors.addressLine2 = "addressLine2.tooLong";
    }
    if (blank(state.postalCode)) {
        errors.postalCode = "postalCode.required";
    }
    else if (tooLong(state.postalCode, MAX_LENGTH.postalCode)) {
        errors.postalCode = "postalCode.tooLong";
    }
    if (blank(state.city)) {
        errors.city = "city.required";
    }
    else if (tooLong(state.city, MAX_LENGTH.city)) {
        errors.city = "city.tooLong";
    }
    if (blank(state.country)) {
        errors.country = "country.required";
    }
    else if (!COUNTRY_RE.test(state.country)) {
        errors.country = "country.invalid";
    }
    if (doc.settings.requirePhone && blank(state.phone)) {
        errors.phone = "phone.required";
    }
    else if (doc.settings.requirePhone &&
        COUNTRY_RE.test(state.country) &&
        state.country !== "FR" &&
        !INTERNATIONAL_PHONE_RE.test(state.phone)) {
        errors.phone = "phone.internationalFormat";
    }
    if (state.donorType === "Organization") {
        if (blank(state.orgName)) {
            errors.orgName = "orgName.required";
        }
        else if (tooLong(state.orgName, MAX_LENGTH.orgName)) {
            errors.orgName = "orgName.tooLong";
        }
        if (blank(state.siret)) {
            errors.siret = "siret.required";
        }
        else if (tooLong(state.siret, MAX_LENGTH.siret)) {
            errors.siret = "siret.tooLong";
        }
        if (blank(state.legalForm)) {
            errors.legalForm = "legalForm.required";
        }
        else if (tooLong(state.legalForm, MAX_LENGTH.legalForm)) {
            errors.legalForm = "legalForm.tooLong";
        }
    }
    // ACTIVE projects only — the engine's config.projects is active-filtered, so the selector and
    // this gate must see the same set.
    const activeProjects = doc.projects.filter((p) => p.isActive);
    const selectorShown = activeProjects.length > 1 ||
        (activeProjects.length >= 1 && doc.settings.showAffectationWhenSingleProject);
    const projectId = state.projectId;
    const eligible = (id) => activeProjects.some((p) => p.projectId.toLowerCase() === id.toLowerCase());
    if (selectorShown && activeProjects.length > 1) {
        if (projectId === null || blank(projectId)) {
            errors.projectId = "projectId.required";
        }
        else if (!UUID_RE.test(projectId)) {
            errors.projectId = "projectId.invalid";
        }
        else if (!eligible(projectId)) {
            errors.projectId = "projectId.notEligible";
        }
    }
    else if (projectId !== null && !blank(projectId)) {
        if (!UUID_RE.test(projectId)) {
            errors.projectId = "projectId.invalid";
        }
        else if (!eligible(projectId)) {
            errors.projectId = "projectId.notEligible";
        }
    }
    // The flag only offers the field; a blank value is always accepted.
    if (doc.settings.enableBirthDate === true &&
        !blank(state.birthDate) &&
        (!isCalendarDate(state.birthDate) || state.birthDate > new Date().toISOString().slice(0, 10))) {
        errors.birthDate = "birthDate.invalid";
    }
    // !== false, not truthiness: an absent key (a document frozen before the flag existed) must
    // still require the consent.
    if (doc.settings.requireConsent !== false && state.gdprConsentGranted !== true) {
        errors.consent = "consent.required";
    }
    return errors;
}
export const STEP_FIELDS = {
    amount: ["amount", "currency", "frequency"],
    details: [
        "donorType",
        "civility",
        "firstName",
        "lastName",
        "email",
        "phone",
        "addressLine1",
        "addressLine2",
        "postalCode",
        "city",
        "country",
        "orgName",
        "siret",
        "legalForm",
        "organization",
        "projectId",
        "birthDate",
        "consent",
    ],
    payment: ["amount", "currency", "frequency"],
};
export function isStepValid(stepId, errors) {
    const fields = STEP_FIELDS[stepId] ?? [];
    return fields.every((f) => errors[f] === undefined);
}
